import React, { ReactNode } from 'react';
import {
	faChartPieAlt,
	faShield,
	faFileSignature,
	faShip,
	faAddressBook,
	faGlobeEurope,
	faFileInvoiceDollar,
	faCheckCircle,
	IconDefinition,
	faMailbox,
	faListCheck,
	faCalculator,
	faHandshake,
	faListTree,
	faPaperPlaneTop,
	faBox,
} from '@fortawesome/pro-duotone-svg-icons';
import {
	faCorner,
	faFileExport,
	faWrench,
} from '@fortawesome/pro-light-svg-icons';
import { Space } from 'antd';
import { Icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FixtureTypes } from '@shared/utils/constants';
import { Links } from '@client/utils/links';

export type MenuItemSection = (
	'HOME' |
	'ADMIN' |
	'FIXTURES' |
	'EXPENSES' |
	'APPROVALS' |
	'COMPANY_SETTINGS' |
	'VESSELS' |
	'CARGOS' |
	'COUNTERPARTIES' |
	'VOYAGES' |
	'MAP' |
	'HEAD_FIXTURES' |
	'ACCOUNTING_ITEMS' |
	'ACCOUNTING'
);

type MenuItem = {
	text: string | ReactNode;
	icon: IconDefinition;
	admin?: boolean;
	hidden?: boolean;
} & (
		{
			link: string;
		} |
		{
			children: Omit<MenuItem, 'icon'>[];
		}
	);

type GetMenuItems = (options: {
	showSpotFixtures: boolean;
	showPerformance: boolean;
}) => Record<MenuItemSection, MenuItem>;

const getMenuItems: GetMenuItems = (_options) => ({
	HOME: {
		text: 'Home',
		link: Links.Home.get(),
		icon: faChartPieAlt,
	},
	ADMIN: {
		text: 'Admin',
		icon: faShield,
		admin: true,
		children: [
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							Organizations
						</Space>
					</>
				),
				link: Links.Organizations.get(),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							Video Guides
						</Space>
					</>
				),
				link: Links.VideoGuides.get(),
			},
		],
	},
	MANAGEMENT: {
		text: 'Management',
		icon: faListCheck,
		management: true,
		children: [
			{
				text: 'Approvals',
				'data-tour': 'menuItemExpenseApproval',
				link: Links.Management.Approvals.get(),
				icon: faCheckCircle,
			},
			{
				text: 'Company Settings',
				link: Links.Management.Settings.get(),
			},
		],
	},
	APPROVALS: {
		management: true,
		text: 'Approvals',
		link: Links.Management.Approvals.get(),
		icon: faCheckCircle,
	},
	COMPANY_SETTINGS: {
		management: true,
		text: 'Company Settings',
		link: Links.Management.Settings.get(),
		icon: faWrench,
	},
	HEAD_FIXTURES: {
		text: 'Head Fixtures',
		icon: faListTree,
		children: [
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							TC-In Estimates
						</Space>
					</>
				),
				link: Links.Fixtures[FixtureTypes.TC_IN].List.get(),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							TC-In Contracts
						</Space>
					</>
				),
				link: Links.Voyages.get('tcIn'),
			},
		],
	},
	FIXTURES: {
		text: 'Estimates',
		'data-tour': 'menuItemFixtures',
		icon: faFileSignature,
		link: Links.Fixtures.out.List.get(),
	},
	EXPENSES: {
		text: 'Vendor Expenses',
		'data-tour': 'menuItemVendorExpenses',
		link: Links.Expenses.get(),
		icon: faFileInvoiceDollar,
	},
	ACCOUNTING_ITEMS: {
		text: 'Postings',
		link: Links.AccountingItems.get(),
		icon: faPaperPlaneTop,
	},
	ACCOUNTING: {
		text: 'Accounting',
		icon: faCalculator,
		children: [
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							Accruals
						</Space>
					</>
				),
				link: Links.Accounting.Accruals.get(),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							Previous Closes
						</Space>
					</>
				),
				link: Links.Accounting.PreviousCloses.get(),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							Mappings
						</Space>
					</>
				),
				link: Links.Accounting.Mapping.get(),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faFileExport as Icon}
							/>
							Export
						</Space>
					</>
				),
				link: Links.Accounting.Export.get(),
			},
		],
	},
	INBOX: {
		text: 'Inbox',
		link: Links.Inbox.get(),
		icon: faMailbox,
	},
	VESSELS: {
		text: 'Vessels',
		'data-tour': 'menuItemVessels',
		link: Links.Vessels.get(),
		icon: faShip,
	},
	COUNTERPARTIES: {
		text: 'Counterparties',
		'data-tour': 'menuItemCounterparties',
		icon: faAddressBook,
		link: Links.Counterparties.get(),
	},
	CARGOS: {
		text: 'Cargo',
		link: Links.Cargos.get(),
		icon: faBox,
	},
	VOYAGES: {
		text: 'Contracts Out',
		'data-tour': 'menuItemVoyages',
		icon: faHandshake,
		children: [
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							TC & VC
						</Space>
					</>
				),
				link: Links.Voyages.get(['spot', 'tcOut']),
			},
			{
				text: (
					<>
						<Space>
							<FontAwesomeIcon
								rotation={180}
								icon={faCorner as Icon}
							/>
							BB
						</Space>
					</>
				),
				key: 'bbHeader',
				children: [
					{
						text: (
							<>
								<Space>
									<FontAwesomeIcon
										rotation={180}
										icon={faCorner as Icon}
									/>
									BB Estimates
								</Space>
							</>
						),
						link: Links.Fixtures.bbOut.List.get(),
					},
					{
						text: (
							<>
								<Space>
									<FontAwesomeIcon
										rotation={180}
										icon={faCorner as Icon}
									/>
									BB Contracts
								</Space>
							</>
						),
						link: Links.Voyages.get('bbOut'),
					},
				],
			},
		],
	},
	MAP: {
		text: 'Map',
		'data-tour': 'menuItemMap',
		icon: faGlobeEurope,
		link: Links.Map.get(),
	},
});

export default getMenuItems;
